// =========================================================
// El Faro — Radar de subvenciones y convocatorias (demo)
//
// Rastreo diario de fuentes web + buzones de newsletters.
// Cada organización ve lo suyo + lo transversal.
// Solo frontend con datos mock (src/mock/faro.js).
// =========================================================
import React from 'react';
import { Icon } from '../icons.jsx';
import { fmtMoney, fmtDateFull, Select } from '../components.jsx';
import { notify } from '../modals.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { runAnalysisFlow } from '../data/repo.js';
import {
  loadFaro, setOppStatus, createFaroSource, toggleFaroSource,
  updateFaroSource, deleteFaroSource,
  saveFaroCriteria, saveFaroAlerts, saveFaroSchedule, runFaroScan,
  fetchOppDocs, sendOppToBid,
} from '../data/faro.js';

const { useState, useMemo, useEffect, useCallback, useRef } = React;

const LOT_OPTIONS = Array.from({ length: 12 }, (_, i) => `Lote ${i + 1}`);

// ---------- helpers ----------
function daysLeft(date) {
  if (!date) return null;
  const d = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86_400_000);
}

const STATUS_META = {
  new:         { label: 'Sin decidir',  sem: 'sem-info' },
  interesting: { label: 'Interesante',  sem: 'sem-success' },
  discarded:   { label: 'Descartada',   sem: 'sem-danger' },
};

const KIND_META = {
  subvencion:   { label: 'Subvención', icon: 'Coins' },
  convocatoria: { label: 'Convocatoria de fundación', icon: 'Crown' },
};

const DEST_META = {
  evento:   { label: 'Evento',   sem: 'sem-lime' },
  proyecto: { label: 'Proyecto', sem: 'sem-violet' },
};

function fit(score) {
  if (score >= 75) return { label: 'Encaje alto', sem: 'sem-success', color: 'var(--sem-success)' };
  if (score >= 55) return { label: 'Revisar',     sem: 'sem-warning', color: 'var(--sem-warning)' };
  return { label: 'Bajo', sem: 'sem-danger', color: 'var(--sem-danger)' };
}


function shortDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: '2-digit' });
}

function hhmm(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

function relDay(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const dl = daysLeft(iso);
  if (dl === 0) return `hoy ${hhmm(iso)}`;
  if (dl === -1) return `ayer ${hhmm(iso)}`;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}

// =========================================================
export function FaroScreen({ demoMode = false }) {
  const auth = useAuth();
  const userId = auth?.user?.id || null;
  const isAdmin = demoMode ? true : !!auth?.isAdmin;

  const FARO = (window.MockData && window.MockData.FARO) || {};

  const [tab, setTab] = useState('opps');
  const [org, setOrg] = useState('all');
  const [loading, setLoading] = useState(!demoMode);
  const [orgs, setOrgs] = useState(() => (demoMode ? FARO.ORGS || [] : [{ id: 'all', name: 'Todas las organizaciones', short: 'Todas' }]));
  const [items, setItems] = useState(() => (demoMode ? (FARO.OPPORTUNITIES || []).map((o) => ({ ...o })) : []));
  const [sources, setSources] = useState(() => (demoMode ? (FARO.SOURCES || []).map((s) => ({ ...s })) : []));
  const [criteria, setCriteria] = useState(() => ({ ...(demoMode ? FARO.CRITERIA || {} : { themes: [], destinations: {} }) }));
  const [alerts, setAlerts] = useState(() => ({ ...(demoMode ? FARO.ALERTS || {} : { include: {}, recipients: [] }) }));
  const [run, setRun] = useState(() => ({ ...(demoMode ? FARO.RUN || {} : {}) }));
  const [schedule, setSchedule] = useState(
    demoMode
      ? { enabled: true, run_hour: 9, run_minute: 0, weekdays: [1, 2, 3, 4, 5], timezone: 'Europe/Madrid', max_pages_per_run: 40, digest_enabled: true, digest_hour: 9, digest_minute: 0 }
      : null,
  );
  const [detail, setDetail] = useState(null);
  const [scanning, setScanning] = useState(false);
  // { kind: 'web' | 'mail', source?: fuente a editar }
  const [sourceForm, setSourceForm] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState('sources');
  const [sendOpp, setSendOpp] = useState(null);
  const [undo, setUndo] = useState(null); // { ids, prev, label }
  const undoTimer = useRef(null);

  const canDecide = demoMode ? true : !!auth?.user && (auth?.roles || []).some((r) => r !== 'viewer');

  // ---------- carga real ----------
  const reload = useCallback(async () => {
    if (demoMode) return;
    setLoading(true);
    try {
      const d = await loadFaro();
      setOrgs(d.orgs);
      setItems(d.opportunities);
      setSources(d.sources);
      setCriteria(d.criteria);
      setAlerts(d.alerts);
      setRun(d.run);
      setSchedule(d.schedule);
    } catch (e) {
      notify(`No se pudo cargar El Faro: ${e.message || e}`, 'AlertTriangle');
    } finally {
      setLoading(false);
    }
  }, [demoMode]);

  useEffect(() => { reload(); }, [reload]);

  // Visibilidad: transversal + organización activa
  const visible = useMemo(
    () => items.filter((o) => o.org === 'all' || org === 'all' || o.org === org),
    [items, org]
  );
  const visibleSources = useMemo(
    () => sources.filter((s) => s.org === 'all' || org === 'all' || s.org === org),
    [sources, org]
  );

  const persist = useCallback((ids, status) => {
    setItems((prev) => prev.map((o) => (ids.includes(o.id) ? { ...o, status } : o)));
    if (!demoMode && ids.length) {
      setOppStatus(ids, status, userId).catch((e) => notify(`No se pudo guardar: ${e.message || e}`, 'AlertTriangle'));
    }
  }, [demoMode, userId]);

  // Decisión con posibilidad de deshacer durante unos segundos.
  const decide = useCallback((ids, status) => {
    if (!canDecide) { notify('Tu rol es de solo lectura.', 'Info'); return; }
    const prev = {};
    items.forEach((o) => { if (ids.includes(o.id)) prev[o.id] = o.status; });
    persist(ids, status);
    setUndo({ ids, prev, label: status === 'interesting' ? 'Marcada como interesante' : 'Descartada' });
    if (undoTimer.current) clearTimeout(undoTimer.current);
    undoTimer.current = setTimeout(() => setUndo(null), 6000);
  }, [canDecide, items, persist]);

  const doUndo = useCallback(() => {
    if (!undo) return;
    const groups = {};
    undo.ids.forEach((id) => {
      const st = undo.prev[id] || 'new';
      (groups[st] = groups[st] || []).push(id);
    });
    Object.entries(groups).forEach(([st, ids]) => persist(ids, st));
    setUndo(null);
  }, [undo, persist]);

  useEffect(() => () => { if (undoTimer.current) clearTimeout(undoTimer.current); }, []);

  const orgName = (id) => (orgs.find((o) => o.id === id) || {}).name || 'Transversal';

  const startScan = async () => {
    if (scanning) return;
    setScanning(true);
    if (demoMode) return;
    try {
      const res = await runFaroScan();
      if (res?.skipped) notify(`Rastreo omitido: ${res.skipped}`, 'Info');
      else notify(`Rastreo completado · ${res?.found ?? 0} oportunidades nuevas`, 'Lighthouse');
      await reload();
    } catch (e) {
      notify(`Error en el rastreo: ${e.message || e}`, 'AlertTriangle');
    } finally {
      setScanning(false);
    }
  };

  // ---------- fuentes: pausar, editar, eliminar ----------
  const toggleSource = useCallback(async (id) => {
    const s = sources.find((x) => x.id === id);
    if (!s) return;
    const next = s.status === 'paused' ? 'active' : 'paused';
    setSources((prev) => prev.map((x) => (x.id === id ? { ...x, status: next } : x)));
    if (demoMode) return;
    try { await toggleFaroSource(id, next); }
    catch (e) { notify(`No se pudo actualizar la fuente: ${e.message || e}`, 'AlertTriangle'); reload(); }
  }, [sources, demoMode, reload]);

  const removeSource = useCallback(async (s) => {
    if (!window.confirm(`¿Eliminar "${s.name}"?\n\nDejará de rastrearse. Las oportunidades ya detectadas se mantienen.`)) return;
    setSources((prev) => prev.filter((x) => x.id !== s.id));
    if (demoMode) { notify('Fuente eliminada (demo)', 'Trash'); return; }
    try { await deleteFaroSource(s.id); notify('Fuente eliminada', 'Trash'); }
    catch (e) { notify(`No se pudo eliminar: ${e.message || e}`, 'AlertTriangle'); reload(); }
  }, [demoMode, reload]);

  const saveSource = useCallback(async (payload) => {
    const editing = sourceForm?.source || null;
    if (editing) {
      setSources((prev) => prev.map((x) => (x.id === editing.id ? { ...x, ...payload } : x)));
      setSourceForm(null);
      if (demoMode) { notify('Fuente actualizada (demo)', 'Save'); return; }
      try {
        const saved = await updateFaroSource(editing.id, payload);
        setSources((prev) => prev.map((x) => (x.id === editing.id ? saved : x)));
        notify('Fuente actualizada', 'Save');
      } catch (e) { notify(`No se pudo guardar: ${e.message || e}`, 'AlertTriangle'); reload(); }
      return;
    }
    setSourceForm(null);
    if (demoMode) {
      setSources((prev) => [{ ...payload, id: `src-${Date.now()}`, found: 0, foundLast: 0, lastRun: null }, ...prev]);
      notify('Fuente añadida a El Faro (demo)', 'CheckCircle');
      return;
    }
    try {
      const created = await createFaroSource(payload, userId);
      setSources((prev) => [created, ...prev]);
      notify('Fuente añadida a El Faro', 'CheckCircle');
    } catch (e) { notify(`No se pudo crear la fuente: ${e.message || e}`, 'AlertTriangle'); }
  }, [sourceForm, demoMode, userId, reload]);

  const finishScan = () => {
    setScanning(false);
    setRun((r) => ({ ...r, lastRunAt: new Date().toISOString() }));
    notify('Rastreo completado · 4 oportunidades nuevas en El Faro', 'Lighthouse');
  };

  // ---------- envío a la fase de análisis ----------
  const confirmSend = async ({ lot, docUrls, files }) => {
    const opp = sendOpp;
    if (!opp) return;
    if (demoMode) {
      setSendOpp(null);
      notify('Enviada a análisis (demo · no se guarda)', 'Sparkles');
      return;
    }
    try {
      const res = await sendOppToBid({ opportunityId: opp.id, lot, docUrls });
      setSendOpp(null);
      setItems((prev) => prev.map((o) => (o.id === opp.id ? { ...o, status: 'interesting', bidId: res.bid_id } : o)));
      if (res.already) { notify('Esta convocatoria ya tenía una licitación creada.', 'Info'); return; }
      (res.warnings || []).slice(0, 2).forEach((w) => notify(w, 'AlertTriangle'));
      // El análisis arranca con el flujo normal de Lizzi (overlay + sondeo).
      runAnalysisFlow({ bidId: res.bid_id, title: opp.title, files: files || [], sourceUrl: opp.url || null, userId })
        .catch(() => {});
    } catch (e) {
      notify(`No se pudo enviar a análisis: ${e.message || e}`, 'AlertTriangle');
    }
  };

  return (
    <div className="viewport-inner fade-in">
      <div className="page-head" style={{ marginBottom: 14, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div className="page-eyebrow">Subvenciones y convocatorias</div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 0 }}>
            <span style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--lime)', color: 'var(--lime-fg)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon.Lighthouse size={20} />
            </span>
            El Faro
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <OrgSelect orgs={orgs} value={org} onChange={setOrg} />
          <button className={`btn ${settingsOpen ? 'btn-primary' : 'btn-outline'}`} onClick={() => setSettingsOpen((v) => !v)}>
            <Icon.Settings size={14} /> Ajustes
          </button>
          {isAdmin && (
            <button className="btn btn-primary" onClick={startScan} disabled={scanning}>
              <span className={scanning ? 'spin' : undefined} style={{ display: 'inline-flex' }}><Icon.Refresh size={14} /></span>
              {scanning ? 'Rastreando…' : 'Rastrear ahora'}
            </button>
          )}
        </div>
      </div>

      <RunBanner run={run} sources={visibleSources} orgLabel={orgName(org)} schedule={schedule} />

      {loading && (
        <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
          Cargando El Faro…
        </div>
      )}

      {!loading && !settingsOpen && (
        <OpportunitiesTab
          items={visible}
          orgs={orgs}
          canDecide={canDecide}
          decide={decide}
          onOpen={setDetail}
          onSend={setSendOpp}
        />
      )}

      {!loading && settingsOpen && (
        <>
          <div style={{ marginBottom: 22 }}>
            <div className="faro-seg">
              {[
                { id: 'sources', label: 'Fuentes web', icon: 'Globe' },
                { id: 'newsletters', label: 'Newsletters', icon: 'Mail' },
                { id: 'criteria', label: 'Criterios', icon: 'Filter' },
                { id: 'alerts', label: 'Automatización y alertas', icon: 'Bell' },
              ].map((t) => {
                const Ico = Icon[t.icon] || Icon.Dot;
                return (
                  <button key={t.id} className={`faro-seg-item ${settingsTab === t.id ? 'active' : ''}`} onClick={() => setSettingsTab(t.id)}>
                    <Ico size={15} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {settingsTab === 'sources' && (
            <SourcesTab
              sources={visibleSources.filter((s) => s.kind !== 'mail')}
              orgs={orgs}
              canEdit={isAdmin}
              onAdd={() => setSourceForm({ kind: 'web' })}
              onEdit={(s) => setSourceForm({ kind: 'web', source: s })}
              onToggle={toggleSource}
              onDelete={removeSource}
            />
          )}
          {settingsTab === 'newsletters' && (
            <NewslettersTab
              sources={visibleSources.filter((s) => s.kind === 'mail')}
              orgs={orgs}
              canEdit={isAdmin}
              onAdd={() => setSourceForm({ kind: 'mail' })}
              onEdit={(s) => setSourceForm({ kind: 'mail', source: s })}
              onToggle={toggleSource}
              onDelete={removeSource}
            />
          )}
          {settingsTab === 'criteria' && (
            <CriteriaTab
              criteria={criteria}
              setCriteria={setCriteria}
              items={visible}
              orgLabel={orgName(org)}
              canEdit={isAdmin}
              onSave={async () => {
                if (demoMode) { notify('Criterios guardados (demo)', 'Save'); return; }
                try { await saveFaroCriteria(criteria, userId); notify('Criterios guardados', 'Save'); }
                catch (e) { notify(`No se pudieron guardar: ${e.message || e}`, 'AlertTriangle'); }
              }}
            />
          )}
          {settingsTab === 'alerts' && (
            <AlertsTab
              alerts={alerts}
              setAlerts={setAlerts}
              orgs={orgs}
              items={visible}
              schedule={schedule}
              canEdit={isAdmin}
              onSaveAlerts={async () => {
                if (demoMode) { notify('Alertas guardadas (demo)', 'Save'); return; }
                try { await saveFaroAlerts(alerts, userId); notify('Alertas guardadas', 'Save'); }
                catch (e) { notify(`No se pudieron guardar: ${e.message || e}`, 'AlertTriangle'); }
              }}
              onSaveSchedule={async (patch) => {
                if (demoMode) { setSchedule((s) => ({ ...(s || {}), ...patch })); return; }
                try {
                  const next = await saveFaroSchedule(patch, userId);
                  setSchedule(next);
                  notify('Automatización actualizada', 'Save');
                } catch (e) { notify(`No se pudo guardar: ${e.message || e}`, 'AlertTriangle'); }
              }}
            />
          )}
        </>
      )}

      {undo && (
        <div className="scale-in faro-undo">
          <Icon.CheckCircle size={15} style={{ color: 'var(--sem-success)' }} />
          <span>{undo.label}{undo.ids.length > 1 ? ` · ${undo.ids.length}` : ''}</span>
          <button className="btn btn-ghost" style={{ marginLeft: 6 }} onClick={doUndo}>
            <Icon.Refresh size={13} /> Deshacer
          </button>
        </div>
      )}

      <OppDrawer
        opp={detail}
        orgs={orgs}
        canDecide={canDecide}
        demoMode={demoMode}
        onClose={() => setDetail(null)}
        onSend={() => { setSendOpp(detail); setDetail(null); }}
        onStatus={(st) => { decide([detail.id], st); setDetail({ ...detail, status: st }); }}
      />


      <SendToBidModal
        opp={sendOpp}
        demoMode={demoMode}
        onClose={() => setSendOpp(null)}
        onConfirm={confirmSend}
      />

      {demoMode && <ScanOverlay open={scanning} steps={run.steps || []} onDone={finishScan} />}
      <SourceFormModal
        form={sourceForm}
        orgs={orgs}
        onClose={() => setSourceForm(null)}
        onSave={saveSource}
      />
    </div>
  );
}


// ---------- helpers de horario ----------
function nextRunLabel(schedule) {
  if (!schedule) return '09:00';
  const h = String(schedule.run_hour ?? 9).padStart(2, '0');
  const m = String(schedule.run_minute ?? 0).padStart(2, '0');
  return `${h}:${m}`;
}

// ---------- selector de organización ----------
function OrgSelect({ orgs, value, onChange }) {
  return (
    <Select icon="Building" value={value} onChange={onChange} minWidth={210}
      options={orgs.map((o) => ({ value: o.id, label: o.id === 'all' ? 'Todas las organizaciones' : o.name }))} />
  );
}

// ---------- banner de última ejecución ----------
function RunBanner({ run, sources, orgLabel, schedule }) {
  const ok = sources.filter((s) => s.status === 'active').length;
  const warn = sources.filter((s) => s.status === 'warning').length;
  const err = sources.filter((s) => s.status === 'error').length;
  const errCount = (run.errors || []).length;
  const Sep = () => <span className="dotsep" />;
  return (
    <div className="faro-strip">
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: 'var(--text-2)' }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: run.status === 'error' ? 'var(--sem-danger)' : 'var(--sem-success)' }} />
        Último rastreo {relDay(run.lastRunAt)}
      </span>
      <Sep />
      <span><strong style={{ color: 'var(--text-2)' }}>{ok}</strong> fuentes activas
        {warn > 0 && <span style={{ color: 'var(--sem-warning)' }}> · {warn} aviso</span>}
        {err > 0 && <span style={{ color: 'var(--sem-danger)' }}> · {err} error</span>}
      </span>
      <Sep />
      <span><strong style={{ color: 'var(--text-2)' }}>{run.found || 0}</strong> nuevas de {run.pagesScanned || 0} páginas</span>
      {errCount > 0 && <><Sep /><span style={{ color: 'var(--sem-warning)' }}>{errCount} incidencias</span></>}
      <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-4)' }}>
        <Icon.Clock size={12} />
        {schedule && schedule.enabled === false ? 'Automatización desactivada' : `Próximo automático · ${nextRunLabel(schedule)}`}
        <Sep />
        {orgLabel}
      </span>
    </div>
  );
}



// =========================================================
// Vista principal · Bandeja / Interesantes / Descartadas
// =========================================================
function fitBand(score) {
  const s = Number(score) || 0;
  return s >= 75 ? 'high' : s >= 55 ? 'mid' : 'low';
}

function OpportunitiesTab({ items, orgs, canDecide, decide, onOpen, onSend }) {
  const [view, setView] = useState('inbox');
  const [kind, setKind] = useState('all');
  const [dest, setDest] = useState('all');
  const [band, setBand] = useState('all');
  const [term, setTerm] = useState('all');
  const [money, setMoney] = useState('all');
  const [origin, setOrigin] = useState('all');
  const [srcName, setSrcName] = useState('all');

  const [sort, setSort] = useState('score');
  const [search, setSearch] = useState('');
  const [more, setMore] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const counts = useMemo(() => ({
    inbox: items.filter((o) => o.status === 'new').length,
    interesting: items.filter((o) => o.status === 'interesting').length,
    discarded: items.filter((o) => o.status === 'discarded').length,
  }), [items]);

  const kpis = useMemo(() => {
    const open = items.filter((o) => (daysLeft(o.deadline) ?? -1) >= 0 && o.status !== 'discarded').length;
    const soon = items.filter((o) => {
      const d = daysLeft(o.deadline);
      return d != null && d >= 0 && d <= 7 && o.status !== 'discarded';
    }).length;
    return { open, soon };
  }, [items]);

  const wanted = view === 'inbox' ? 'new' : view === 'interesting' ? 'interesting' : 'discarded';

  // Base de la vista actual: sirve para contar cuántas hay en cada nivel de encaje.
  const inView = useMemo(() => items.filter((o) => o.status === wanted), [items, wanted]);
  const bandCounts = useMemo(() => ({
    all: inView.length,
    high: inView.filter((o) => fitBand(o.score) === 'high').length,
    mid: inView.filter((o) => fitBand(o.score) === 'mid').length,
    low: inView.filter((o) => fitBand(o.score) === 'low').length,
  }), [inView]);

  const list = useMemo(() => {
    const filtered = inView.filter((o) => {
      if (kind !== 'all' && o.kind !== kind) return false;
      if (dest !== 'all' && o.destination !== dest) return false;
      if (band !== 'all' && fitBand(o.score) !== band) return false;
      if (origin !== 'all' && (o.sourceKind || 'web') !== origin) return false;
      if (srcName !== 'all' && (o.sourceName || '—') !== srcName) return false;

      if (money !== 'all') {
        const a = o.amount;
        if (money === 'unknown' && a != null) return false;
        if (money === 'any' && a == null) return false;
        if (money === '50k' && (a == null || a < 50_000)) return false;
        if (money === '200k' && (a == null || a < 200_000)) return false;
      }
      if (term !== 'all') {
        const d = daysLeft(o.deadline);
        if (term === 'open' && !(d != null && d >= 0)) return false;
        if (term === '7' && !(d != null && d >= 0 && d <= 7)) return false;
        if (term === '30' && !(d != null && d >= 0 && d <= 30)) return false;
        if (term === 'expired' && !(d != null && d < 0)) return false;
      }
      if (search) {
        const hay = `${o.title} ${o.funder} ${(o.tags || []).join(' ')} ${o.sourceName}`.toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }
      return true;
    });
    const far = 9e15;
    const by = {
      score: (a, b) => (Number(b.score) || 0) - (Number(a.score) || 0),
      deadline: (a, b) => (a.deadline ? new Date(a.deadline).getTime() : far) - (b.deadline ? new Date(b.deadline).getTime() : far),
      amount: (a, b) => (b.amount ?? -1) - (a.amount ?? -1),
      recent: (a, b) => new Date(b.detectedAt || 0) - new Date(a.detectedAt || 0),
      published: (a, b) => (b.published ? new Date(b.published).getTime() : -far) - (a.published ? new Date(a.published).getTime() : -far),
    };
    return filtered.slice().sort(by[sort] || by.score);
  }, [inView, kind, dest, band, origin, srcName, money, term, search, sort]);

  // Fuentes presentes en la vista actual (web y newsletters)
  const srcOptions = useMemo(() => {
    const map = new Map();
    inView.forEach((o) => {
      const n = o.sourceName || '—';
      map.set(n, (map.get(n) || 0) + 1);
    });
    return [{ v: 'all', l: 'Todas las fuentes' },
      ...[...map.entries()].sort((a, b) => b[1] - a[1]).map(([n, c]) => ({ v: n, l: `${n} (${c})` }))];
  }, [inView]);

  useEffect(() => { setSelected(new Set()); }, [view]);
  useEffect(() => {
    if (srcName !== 'all' && !srcOptions.some((o) => o.v === srcName)) setSrcName('all');
  }, [srcOptions, srcName]);

  const activeFilters = [kind, dest, band, term, money, origin, srcName].filter((v) => v !== 'all').length + (search ? 1 : 0);
  const clearFilters = () => { setKind('all'); setDest('all'); setBand('all'); setTerm('all'); setMoney('all'); setOrigin('all'); setSrcName('all'); setSearch(''); };


  const selArr = [...selected];
  const apply = (st) => { decide(selArr, st); setSelected(new Set()); };

  const views = [
    { id: 'inbox', label: 'Bandeja', icon: 'Lighthouse', count: counts.inbox },
    { id: 'interesting', label: 'Interesantes', icon: 'Star', count: counts.interesting },
    { id: 'discarded', label: 'Descartadas', icon: 'Trash', count: counts.discarded },
  ];

  const bands = [
    { id: 'all', label: 'Cualquier encaje', color: 'var(--text-3)' },
    { id: 'high', label: 'Encaje alto', color: 'var(--sem-success)' },
    { id: 'mid', label: 'Revisar', color: 'var(--sem-warning)' },
    { id: 'low', label: 'Encaje bajo', color: 'var(--text-4)' },
  ];

  const allVisibleSelected = list.length > 0 && list.every((o) => selected.has(o.id));

  const FILTERS = [
    { key: 'term', value: term, set: setTerm, icon: 'Clock', title: 'Plazo de cierre', options: [
      { v: 'all', l: 'Cualquier plazo' },
      { v: '7', l: 'Cierra en 7 días' },
      { v: '30', l: 'Cierra en 30 días' },
      { v: 'open', l: 'Solo abiertas' },
      { v: 'expired', l: 'Ya cerradas' },
    ] },
    { key: 'money', value: money, set: setMoney, icon: 'Euro', title: 'Dotación', options: [
      { v: 'all', l: 'Cualquier dotación' },
      { v: 'any', l: 'Con dotación publicada' },
      { v: '50k', l: 'Desde 50.000 €' },
      { v: '200k', l: 'Desde 200.000 €' },
      { v: 'unknown', l: 'Sin dotación publicada' },
    ] },
    { key: 'kind', value: kind, set: setKind, icon: 'Layers', title: 'Tipo', options: [
      { v: 'all', l: 'Todos los tipos' },
      { v: 'subvencion', l: 'Subvenciones' },
      { v: 'convocatoria', l: 'Convocatorias' },
    ] },
    { key: 'dest', value: dest, set: setDest, icon: 'Target', title: 'Destino', options: [
      { v: 'all', l: 'Evento y proyecto' },
      { v: 'evento', l: 'Solo eventos' },
      { v: 'proyecto', l: 'Solo proyectos' },
    ] },
    { key: 'origin', value: origin, set: setOrigin, icon: 'Globe', title: 'Origen', options: [
      { v: 'all', l: 'Todos los orígenes' },
      { v: 'web', l: 'Solo webs' },
      { v: 'mail', l: 'Solo newsletters' },
    ] },
    { key: 'srcName', value: srcName, set: setSrcName, icon: 'Compass', title: 'Fuente / newsletter', options: srcOptions },
  ];
  const advActive = FILTERS.filter((f) => f.value !== 'all');
  const activeChips = [
    ...(band !== 'all' ? [{ key: 'band', label: (bands.find((b) => b.id === band) || {}).label, clear: () => setBand('all') }] : []),
    ...advActive.map((f) => ({
      key: f.key,
      label: (f.options.find((o) => o.v === f.value) || {}).l,
      clear: () => f.set('all'),
    })),
    ...(search ? [{ key: 'search', label: `“${search}”`, clear: () => setSearch('') }] : []),
  ];

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginBottom: 26 }}>
        <FunnelStat icon="Lighthouse" label="Por decidir" value={counts.inbox} color="var(--sem-info)" />
        <FunnelStat icon="Star" label="Interesantes" value={counts.interesting} color="var(--sem-success)" />
        <FunnelStat icon="Clock" label="Cierran esta semana" value={kpis.soon} color="var(--sem-warning)" />
        <FunnelStat icon="Compass" label="Abiertas ahora" value={kpis.open} color="var(--primary)" />
      </div>

      {/* Barra de trabajo: bandeja activa · búsqueda · orden */}
      <div className="faro-toolbar">
        <div className="faro-seg" role="tablist" aria-label="Bandejas">
          {views.map((t) => {
            const Ico = Icon[t.icon] || Icon.Dot;
            return (
              <button key={t.id} role="tab" aria-selected={view === t.id}
                className={`faro-seg-item ${view === t.id ? 'active' : ''}`} onClick={() => setView(t.id)}>
                <Ico size={13} /> {t.label} <span className="n">{t.count}</span>
              </button>
            );
          })}
        </div>
        <div className="faro-search">
          <Icon.Search size={13} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar convocatoria, convocante o etiqueta…" />
          {search && (
            <button type="button" aria-label="Borrar búsqueda" onClick={() => setSearch('')}><Icon.X size={12} /></button>
          )}
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <SoftSelect value={sort} onChange={setSort} icon="GanttChart" options={[
            { v: 'score', l: 'Mejor encaje' },
            { v: 'deadline', l: 'Cierra antes' },
            { v: 'amount', l: 'Mayor dotación' },
            { v: 'published', l: 'Publicación más reciente' },
            { v: 'recent', l: 'Más reciente' },
          ]} />
        </div>
      </div>

      {/* Panel de filtros: encaje siempre visible, el resto desplegable */}
      <div className="faro-filters">
        <div className="faro-filters-row">
          <span className="faro-filters-label"><Icon.Target size={12} /> Nivel de encaje</span>
          <div className="faro-seg faro-seg-quiet" role="group" aria-label="Nivel de encaje">
            {bands.map((b) => (
              <button key={b.id} className={`faro-seg-item ${band === b.id ? 'active' : ''}`} onClick={() => setBand(b.id)}>
                {b.id !== 'all' && <span className="dot" style={{ background: b.color }} />}
                {b.label} <span className="n">{bandCounts[b.id]}</span>
              </button>
            ))}
          </div>
          <button type="button" className={`faro-more ${more ? 'active' : ''}`} onClick={() => setMore((v) => !v)}
            aria-expanded={more}>
            <Icon.Filter size={13} /> Más filtros
            {advActive.length > 0 && <span className="n">{advActive.length}</span>}
            <span className={`chev ${more ? 'up' : ''}`}><Icon.ChevronDown size={13} /></span>
          </button>
        </div>

        {more && (
          <div className="faro-filters-grid scale-in">
            {FILTERS.map((f) => (
              <label key={f.key} className="faro-field">
                <span>{f.title}</span>
                <SoftSelect value={f.value} onChange={f.set} icon={f.icon} options={f.options} />
              </label>
            ))}
          </div>
        )}

        {activeChips.length > 0 && (
          <div className="faro-filters-active">
            {activeChips.map((c) => (
              <button key={c.key} type="button" className="faro-chip-x" onClick={c.clear}>
                {c.label} <Icon.X size={11} />
              </button>
            ))}
            <button type="button" className="faro-clear" onClick={clearFilters}>Quitar todos</button>
            <span className="faro-filters-count">{list.length} de {inView.length}</span>
          </div>
        )}
      </div>



      {selArr.length > 0 && canDecide && (
        <div className="scale-in" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', marginBottom: 14, borderRadius: 12, background: 'var(--bg-2)', border: '1px solid var(--line-2)' }}>
          <span style={{ fontSize: 13, color: 'var(--text-2)' }}><strong>{selArr.length}</strong> seleccionada{selArr.length === 1 ? '' : 's'}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {view !== 'discarded' && <button className="btn btn-outline" onClick={() => apply('discarded')}><Icon.X size={13} /> Descartar</button>}
            {view !== 'interesting' && <button className="btn btn-primary" onClick={() => apply('interesting')}><Icon.Star size={13} /> Interesante</button>}
            {view !== 'inbox' && <button className="btn btn-ghost" onClick={() => apply('new')}><Icon.Refresh size={13} /> A la bandeja</button>}
          </div>
        </div>
      )}

      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: '1px solid var(--line-1)', background: 'var(--bg-1)' }}>
          <input type="checkbox" checked={allVisibleSelected}
            onChange={() => setSelected(allVisibleSelected ? new Set() : new Set(list.map((o) => o.id)))} />
          <span className="section-title">{list.length} convocatoria{list.length === 1 ? '' : 's'}</span>
        </div>
        {/* Encabezados de columna: qué significa cada dato de la fila */}
        <div className="faro-list">
          {list.length > 0 && (
            <div className="faro-head" aria-hidden="true">
              <span />
              <span>Convocatoria</span>
              <span className="hide-lg">Origen</span>
              <span className="hide-md">Dotación</span>
              <span className="hide-lg">Publicada</span>
              <span>Cierra</span>
              <span className="hide-md">Encaje</span>
              {canDecide && <span style={{ textAlign: 'right' }}>Acciones</span>}
            </div>
          )}
          {list.map((o, i) => (
            <OppCard key={o.id} opp={o} orgs={orgs} index={i}
              canDecide={canDecide}
              checked={selected.has(o.id)}
              onToggle={() => setSelected((prev) => { const n = new Set(prev); n.has(o.id) ? n.delete(o.id) : n.add(o.id); return n; })}
              onDecide={(st) => decide([o.id], st)}
              onSend={() => onSend(o)}
              onOpen={() => onOpen(o)} />
          ))}
        </div>

        {list.length === 0 && (
          <div style={{ padding: 72, textAlign: 'center', color: 'var(--text-3)' }}>
            <Icon.Lighthouse size={30} style={{ opacity: 0.5 }} />
            <div style={{ marginTop: 12, fontSize: 13 }}>
              {view === 'inbox' ? 'Todo decidido. El Faro avisará cuando llegue algo nuevo.' : 'Nada por aquí todavía.'}
            </div>
          </div>
        )}
      </div>
    </>
  );
}


function SoftSelect({ value, onChange, options, icon }) {
  return (
    <Select size="sm" icon={Icon[icon] ? icon : 'Filter'} value={value} onChange={onChange} options={options} />
  );
}

function FunnelStat({ icon, label, value, color }) {
  const Ico = Icon[icon] || Icon.Dot;
  return (
    <div className="faro-stat">
      <span className="ic" style={{ color }}><Ico size={18} /></span>
      <div className="v" style={{ color: 'var(--text-1)' }}>{value}</div>
      <div className="l">{label}</div>
    </div>
  );
}

// Anillo de encaje · visual, sin texto extra
function ScoreRing({ score, color }) {
  const r = 14, c = 2 * Math.PI * r;
  return (
    <svg className="faro-ring" viewBox="0 0 34 34">
      <circle cx="17" cy="17" r={r} fill="none" stroke="var(--line-1)" strokeWidth="3" />
      <circle cx="17" cy="17" r={r} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c - (c * Math.max(0, Math.min(100, score))) / 100}
        transform="rotate(-90 17 17)" style={{ transition: 'stroke-dashoffset 600ms cubic-bezier(0.16,1,0.3,1)' }} />
      <text x="17" y="20.5" textAnchor="middle" style={{ fontSize: 10, fill: 'var(--text-2)', fontFamily: 'JetBrains Mono, monospace' }}>{score}</text>
    </svg>
  );
}

function OppCard({ opp, orgs, checked, onToggle, onOpen, onDecide, onSend, canDecide, index = 0 }) {
  const kd = KIND_META[opp.kind] || KIND_META.subvencion;
  const f = fit(opp.score);
  const dl = daysLeft(opp.deadline);
  const orgObj = orgs.find((o) => o.id === opp.org) || {};
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

  return (
    <div className="faro-card row-enter" onClick={onOpen}
      style={{ animationDelay: `${Math.min(index, 8) * 35}ms`, background: checked ? 'var(--bg-active)' : undefined }}>
      <span onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={checked} onChange={onToggle} />
      </span>

      <div style={{ minWidth: 0 }}>
        <div className="title">{opp.title}</div>
        <div className="meta">
          <span>{opp.funder}</span>
          <span className="dotsep" style={{ width: 4, height: 4, borderRadius: 999, background: 'var(--line-3)' }} />
          <span>{kd.label}</span>
          <span className="dotsep" style={{ width: 4, height: 4, borderRadius: 999, background: 'var(--line-3)' }} />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            {opp.org === 'all' ? <Icon.Globe size={11} /> : <Icon.Building size={11} />}
            {opp.org === 'all' ? 'Transversal' : (orgObj.short || orgObj.name || '—')}
          </span>
          {opp.bidId && (
            <><span className="dotsep" style={{ width: 4, height: 4, borderRadius: 999, background: 'var(--line-3)' }} />
            <span className="sem sem-violet" style={{ fontSize: 10 }}>En análisis</span></>
          )}
        </div>
      </div>

      <div className="hide-lg" data-label="Origen" style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {opp.sourceKind === 'mail' ? <Icon.Mail size={12} /> : <Icon.Globe size={12} />}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opp.sourceName || '—'}</span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 3 }}>{opp.sourceKind === 'mail' ? 'Newsletter' : 'Web'}</div>
      </div>

      <div className="hide-md" data-label="Dotación">
        <div className="font-mono" style={{ fontSize: 13, color: opp.amount == null ? 'var(--text-4)' : 'var(--text-1)' }}>
          {opp.amount == null ? 'No publicada' : fmtMoney(opp.amount)}
        </div>
      </div>

      <div className="hide-lg" data-label="Publicada">
        <div style={{ fontSize: 12.5, color: opp.published ? 'var(--text-1)' : 'var(--text-4)' }}>
          {opp.published ? shortDate(opp.published) : '—'}
        </div>
        {opp.publishedApprox && (
          <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 3 }}>aprox.</div>
        )}
      </div>

      <div data-label="Cierra">
        <div style={{ fontSize: 12.5, color: 'var(--text-1)' }}>{shortDate(opp.deadline)}</div>
        <div style={{ fontSize: 11, marginTop: 3, color: dl != null && dl <= 10 ? 'var(--sem-warning)' : 'var(--text-4)' }}>
          {dl != null ? (dl >= 0 ? `en ${dl} días` : 'cerrada') : 'sin fecha'}
        </div>
      </div>

      <div className="faro-score hide-md">
        <ScoreRing score={opp.score || 0} color={f.color} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, color: f.color, fontWeight: 600, whiteSpace: 'nowrap' }}>{f.label}</div>
          <div style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 2, whiteSpace: 'nowrap' }}>{relDay(opp.detectedAt)}</div>
        </div>
      </div>


      {canDecide && (
        <div className="faro-actions" onClick={(e) => e.stopPropagation()}>
          {opp.status === 'interesting' && !opp.bidId && (
            <button className="btn btn-primary" style={{ padding: '6px 11px', fontSize: 12 }} onClick={stop(onSend)} title="Enviar a análisis">
              <Icon.Sparkles size={13} /> Analizar
            </button>
          )}
          {opp.status !== 'interesting' && (
            <button className="faro-act star" onClick={stop(() => onDecide('interesting'))} title="Marcar como interesante" aria-label="Marcar como interesante">
              <Icon.Star size={15} />
            </button>
          )}
          {opp.status !== 'discarded' && (
            <button className="faro-act del" onClick={stop(() => onDecide('discarded'))} title="Descartar" aria-label="Descartar">
              <Icon.X size={15} />
            </button>
          )}
          {opp.status === 'discarded' && (
            <button className="faro-act" onClick={stop(() => onDecide('new'))} title="Devolver a la bandeja" aria-label="Devolver a la bandeja">
              <Icon.Refresh size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// =========================================================
// Drawer de ficha
// =========================================================
function OppDrawer({ opp, orgs, onClose, onStatus, onSend, canDecide = true, demoMode = false }) {
  const [docs, setDocs] = useState([]);
  const [docsBusy, setDocsBusy] = useState(false);
  const [docsNote, setDocsNote] = useState('');
  const [docsAt, setDocsAt] = useState(null);

  useEffect(() => {
    if (!opp) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [opp, onClose]);

  const loadDocs = useCallback((force = false) => {
    if (!opp) return;
    if (!opp.url) { setDocsNote('Esta convocatoria no tiene enlace público.'); return; }
    if (demoMode) { setDocsNote('En modo demo no se descargan documentos reales.'); return; }
    setDocsBusy(true); setDocsNote('');
    fetchOppDocs(opp.url, force ? opp.id : opp.id)
      .then((res) => {
        const found = (res?.documents || res?.docs || []).filter((d) => d.url);
        setDocs(found);
        setDocsAt(new Date().toISOString());
        setDocsNote(found.length ? '' : (res?.note || 'No se encontraron documentos descargables en la página.'));
      })
      .catch((e) => setDocsNote(`No se pudo leer la página: ${e.message || e}`))
      .finally(() => setDocsBusy(false));
  }, [opp?.id, opp?.url, demoMode]);

  useEffect(() => {
    setDocsNote(''); setDocsBusy(false);
    if (!opp) { setDocs([]); setDocsAt(null); return; }
    const cached = (opp.documents || []).filter((d) => d && d.url);
    setDocs(cached);
    setDocsAt(opp.documentsFetchedAt || null);
    // Los documentos se guardan durante el rastreo: aquí solo mostramos
    // lo guardado. Si una ficha antigua no los tiene, se buscan a mano.
    if (!opp.documentsFetchedAt && !cached.length) {
      setDocsNote('Esta ficha es anterior al guardado automático. Pulsa «Buscar documentos».');
    }
  }, [opp?.id]);



  if (!opp) return null;

  const st = STATUS_META[opp.status] || STATUS_META.new;
  const kd = KIND_META[opp.kind] || KIND_META.subvencion;
  const de = DEST_META[opp.destination] || DEST_META.proyecto;
  const f = fit(opp.score);
  const dl = daysLeft(opp.deadline);
  const orgObj = orgs.find((o) => o.id === opp.org) || {};

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)', zIndex: 320, display: 'flex', justifyContent: 'flex-end' }}>
      <div className="scale-in" onClick={(e) => e.stopPropagation()}
        style={{ width: 560, maxWidth: '100%', height: '100%', background: 'var(--bg-1)', borderLeft: '1px solid var(--line-2)', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-3, 0 24px 60px rgba(0,0,0,0.35))' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--line-1)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              <span className="chip" style={{ fontSize: 10 }}>{kd.label}</span>
              <span className={`sem ${de.sem}`} style={{ fontSize: 10 }}>{de.label}</span>
              <span className={`sem ${st.sem}`} style={{ fontSize: 10 }}>{st.label}</span>
            </div>
            <div style={{ fontSize: 17, fontWeight: 600, lineHeight: 1.3 }}>{opp.title}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 4 }}>{opp.funder} · {opp.org === 'all' ? 'Transversal' : (orgObj.name || '')}</div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><Icon.X size={16} /></button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            <MiniFact icon="Coins" label="Dotación" value={opp.amount == null ? 'No publicada' : fmtMoney(opp.amount)} sub={opp.coFunding || (opp.amount == null ? 'La fuente no indica importe' : null)} />
            <MiniFact icon="Clock" label="Cierre" value={fmtDateFull(opp.deadline)} sub={dl != null ? (dl >= 0 ? `Quedan ${dl} días` : 'Cerrada') : '—'} />
            <MiniFact icon="Calendar" label="Publicación"
              value={opp.published ? `${fmtDateFull(opp.published)}${opp.publishedApprox ? ' (aprox.)' : ''}` : 'No publicada'}
              sub={`Detectada ${relDay(opp.detectedAt)}`} />
            <MiniFact icon="Globe" label="Ámbito" value={opp.scope} sub={(opp.tags || []).join(' · ')} />
          </div>

          <DrawerBlock title="Resumen">
            <p style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-2)' }}>{opp.summary}</p>
          </DrawerBlock>

          <DrawerBlock title="Requisitos principales">
            <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(opp.requirements || []).map((r, i) => (
                <li key={i} style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>{r}</li>
              ))}
            </ul>
          </DrawerBlock>

          <DrawerBlock title="Por qué El Faro la propone" badge={`Encaje ${opp.score}/100 · ${f.label}`} badgeSem={f.sem}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(opp.matched || []).map((m, i) => (
                <div key={`m${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-2)' }}>
                  <Icon.CheckCircle size={13} style={{ color: 'var(--sem-success)', flexShrink: 0 }} /> {m}
                </div>
              ))}
              {(opp.unmatched || []).map((m, i) => (
                <div key={`u${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-3)' }}>
                  <Icon.AlertTriangle size={13} style={{ color: 'var(--sem-warning)', flexShrink: 0 }} /> {m}
                </div>
              ))}
            </div>
          </DrawerBlock>

          <DrawerBlock title="Texto literal de la fuente" badge={opp.sourceKind === 'mail' ? 'Newsletter' : 'Web'} badgeSem="sem-neutral">
            <blockquote style={{ margin: 0, padding: '12px 14px', borderLeft: '3px solid var(--line-2)', background: 'var(--bg-2)', borderRadius: 8, fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-2)' }}>
              {opp.excerpt}
            </blockquote>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12, color: 'var(--text-3)' }}>
              {opp.sourceKind === 'mail' ? <Icon.Mail size={13} /> : <Icon.Globe size={13} />} {opp.sourceName}
            </div>
            {opp.mailSubject && (
              <div style={{ marginTop: 10, padding: '10px 12px', border: '1px solid var(--line-2)', borderRadius: 9, fontSize: 12, color: 'var(--text-3)', lineHeight: 1.6 }}>
                <div style={{ color: 'var(--text-2)', fontWeight: 600 }}>{opp.mailSubject}</div>
                <div>{opp.mailFrom} · {relDay(opp.mailReceivedAt)}</div>
                {opp.mailLink && (
                  <a className="btn btn-ghost" href={opp.mailLink} target="_blank" rel="noreferrer noopener"
                    style={{ marginTop: 6, fontSize: 11.5, padding: '4px 8px' }}>
                    <Icon.Mail size={12} /> Ver el correo en Gmail
                  </a>
                )}
              </div>
            )}
          </DrawerBlock>

          <DrawerBlock
            title="Documentos de la convocatoria"
            badge={docsBusy ? 'Buscando…' : `${docs.length} encontrado${docs.length === 1 ? '' : 's'}`}
            badgeSem={docsBusy ? 'sem-neutral' : (docs.length ? 'sem-violet' : 'sem-warning')}>
            {docs.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {docs.map((d) => (
                  <a key={d.url} href={d.url} target="_blank" rel="noopener noreferrer nofollow"
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--line-2)', borderRadius: 10, background: 'var(--bg-2)', textDecoration: 'none' }}>
                    {d.kind === 'page'
                      ? <Icon.Link size={14} style={{ color: 'var(--text-4)', flexShrink: 0 }} />
                      : <Icon.FileText size={14} style={{ color: 'var(--text-4)', flexShrink: 0 }} />}
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 13, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
                      {d.via ? <span style={{ display: 'block', fontSize: 11, color: 'var(--text-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>vía {d.via}</span> : null}
                    </span>
                    {d.size ? <span className="font-mono" style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-4)' }}>{Math.round(d.size / 1024)} KB</span> : null}
                    <Icon.ArrowUpRight size={13} style={{ color: 'var(--text-4)', flexShrink: 0, marginLeft: d.size ? 0 : 'auto' }} />
                  </a>
                ))}

                <div style={{ fontSize: 11.5, color: 'var(--text-4)', marginTop: 2 }}>Al enviar a análisis podrás elegir cuáles se descargan.</div>
              </div>
            ) : (
              <div className={docsBusy ? 'sem sem-neutral' : 'sem sem-warning'} style={{ fontSize: 12.5, padding: '10px 12px', borderRadius: 9 }}>
                <Icon.Info size={13} /> {docsBusy ? 'Buscando documentos en la página oficial…' : (docsNote || 'Sin documentos detectados.')}
              </div>
            )}
            {!demoMode && opp.url && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>
                  {docsBusy ? 'Buscando…' : (docsAt ? `Guardados durante el rastreo · ${fmtDateFull(docsAt)}` : 'Sin documentos guardados')}
                </span>
                <button className="btn btn-ghost" style={{ marginLeft: 'auto', padding: '5px 10px', fontSize: 12 }}
                  disabled={docsBusy} onClick={() => loadDocs(true)}>
                  <Icon.Refresh size={13} /> {docsAt ? 'Volver a buscar' : 'Buscar documentos'}
                </button>
              </div>
            )}

          </DrawerBlock>



          {opp.discardReason && (
            <div className="sem sem-danger" style={{ fontSize: 12.5, padding: '10px 12px', borderRadius: 9 }}>
              <Icon.Info size={13} /> {opp.discardReason}
            </div>
          )}
        </div>

        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--line-1)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <a className="btn btn-outline" href={opp.url} target="_blank" rel="noopener noreferrer nofollow"><Icon.ArrowUpRight size={14} /> Fuente original</a>
          {canDecide && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              {opp.status !== 'discarded' && <button className="btn btn-outline" onClick={() => onStatus('discarded')}><Icon.X size={13} /> Descartar</button>}
              {opp.status !== 'interesting' && <button className="btn btn-primary" onClick={() => onStatus('interesting')}><Icon.Star size={13} /> Interesante</button>}
              {opp.status === 'interesting' && !opp.bidId && <button className="btn btn-primary" onClick={onSend}><Icon.Sparkles size={13} /> Enviar a análisis</button>}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

function DrawerBlock({ title, badge, badgeSem = 'sem-violet', children }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div className="section-title">{title}</div>
        {badge && <span className={`sem ${badgeSem}`} style={{ fontSize: 10.5, marginLeft: 'auto' }}>{badge}</span>}
      </div>
      {children}
    </div>
  );
}

function MiniFact({ icon, label, value, sub }) {
  const Ico = Icon[icon] || Icon.Dot;
  return (
    <div style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid var(--line-1)', background: 'var(--bg-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-4)', fontWeight: 600 }}>
        <Ico size={12} /> {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)', marginTop: 5 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 3, lineHeight: 1.45 }}>{sub}</div>}
    </div>
  );
}

// =========================================================
// Pestaña 2 · Fuentes
// =========================================================
const SOURCE_STATUS = {
  active:  { label: 'Activa',  sem: 'sem-success' },
  warning: { label: 'Aviso',   sem: 'sem-warning' },
  error:   { label: 'Error',   sem: 'sem-danger' },
  paused:  { label: 'Pausada', sem: 'sem-neutral' },
};

function SourcesTab({ sources, orgs, onAdd, onEdit, onToggle, onDelete, canEdit = true }) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <p className="muted" style={{ fontSize: 13, maxWidth: 640 }}>
          Páginas que Lizzi rastrea cada mañana. Cada fuente aplica a <strong>todas las organizaciones</strong> o solo a una.
        </p>
        {canEdit && <button className="btn btn-primary" style={{ marginLeft: 'auto', flexShrink: 0 }} onClick={onAdd}><Icon.Plus size={14} /> Añadir fuente web</button>}
      </div>

      <SourceGroup title="Fuentes web" subtitle="Rastreo diario de convocatorias" icon="Globe"
        list={sources} orgs={orgs} canEdit={canEdit} onEdit={onEdit} onToggle={onToggle} onDelete={onDelete} />
    </>
  );
}

// =========================================================
// Pestaña 3 · Newsletters (buzones de correo)
// =========================================================
function NewslettersTab({ sources, orgs, onAdd, onEdit, onToggle, onDelete, canEdit = true }) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <p className="muted" style={{ fontSize: 13, maxWidth: 640 }}>
          Buzones donde llegan los boletines. Lizzi los lee cada mañana y extrae las convocatorias que contienen.
        </p>
        {canEdit && <button className="btn btn-primary" style={{ marginLeft: 'auto', flexShrink: 0 }} onClick={onAdd}><Icon.Plus size={14} /> Añadir buzón</button>}
      </div>

      <SourceGroup title="Buzones de newsletters" subtitle="Lectura diaria del correo" icon="Mail"
        list={sources} orgs={orgs} canEdit={canEdit} onEdit={onEdit} onToggle={onToggle} onDelete={onDelete} />
    </>
  );
}

function SourceGroup({ title, subtitle, icon, list, orgs, onToggle, onEdit, onDelete, canEdit = true }) {
  const Ico = Icon[icon] || Icon.Dot;
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
        <Ico size={15} style={{ color: 'var(--text-3)' }} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{subtitle}</div>
        </div>
        <span className="chip" style={{ marginLeft: 'auto', fontSize: 10.5 }}>{list.length}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
        {list.map((s) => {
          const st = SOURCE_STATUS[s.status] || SOURCE_STATUS.active;
          const org = orgs.find((o) => o.id === s.org) || {};
          return (
            <div key={s.id} className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span className={`sem ${st.sem}`} style={{ width: 32, height: 32, borderRadius: 9, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {s.kind === 'mail' ? <Icon.Mail size={15} /> : <Icon.Globe size={15} />}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 }}>{s.name}</div>
                  <div className="font-mono" style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.url}</div>
                </div>
                <span className={`sem ${st.sem}`} style={{ fontSize: 10 }}>{st.label}</span>
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span className="chip" style={{ fontSize: 10 }}>
                  {s.org === 'all' ? <Icon.Globe size={10} /> : <Icon.Building size={10} />} {s.org === 'all' ? 'Transversal' : (org.short || org.name)}
                </span>
                <span className="chip" style={{ fontSize: 10 }}><Icon.Refresh size={10} /> {s.freq === 'daily' ? 'Diaria' : s.freq === 'weekly' ? 'Semanal' : 'Manual'}</span>
                <span className="chip" style={{ fontSize: 10 }}><Icon.Compass size={10} /> {s.found} detectadas</span>
              </div>

              {s.senders && (
                <div style={{ fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.5 }}>
                  <strong style={{ color: 'var(--text-2)' }}>Remitentes:</strong> {s.senders.join(', ')}
                </div>
              )}
              {s.lastMail && (
                <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}><Icon.Mail size={11} /> Último correo leído {relDay(s.lastMail)}</div>
              )}
              {s.note && (
                <div className={`sem ${st.sem}`} style={{ fontSize: 11.5, lineHeight: 1.45, alignItems: 'flex-start', maxWidth: '100%', whiteSpace: 'normal' }}>
                  <Icon.AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} /> {s.note}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto', paddingTop: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--text-4)' }}>Última revisión {s.lastRun ? relDay(s.lastRun) : '—'} · +{s.foundLast} nuevas</span>
                {canEdit && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
                    <button className="btn btn-ghost" style={{ fontSize: 11.5, padding: '4px 8px' }} onClick={() => onToggle(s.id)}>
                      {s.status === 'paused' ? <><Icon.Check size={12} /> Reactivar</> : <><Icon.Clock size={12} /> Pausar</>}
                    </button>
                    <button className="btn btn-ghost btn-icon" title="Editar" aria-label="Editar" onClick={() => onEdit && onEdit(s)}>
                      <Icon.Edit size={13} />
                    </button>
                    <button className="btn btn-ghost btn-icon" title="Eliminar" aria-label="Eliminar"
                      style={{ color: 'var(--sem-danger)' }} onClick={() => onDelete && onDelete(s)}>
                      <Icon.Trash size={13} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {list.length === 0 && (
        <div className="card" style={{ padding: 34, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
          No hay fuentes de este tipo para la organización seleccionada.
        </div>
      )}
    </div>
  );
}

// ---------- Modal: añadir / editar fuente ----------
function SourceFormModal({ form, orgs, onClose, onSave }) {
  const src = form?.source || null;
  const kind = form?.kind || 'web';
  const isMail = kind === 'mail';
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [org, setOrg] = useState('all');
  const [freq, setFreq] = useState('daily');
  const [senders, setSenders] = useState('');
  const [mailLabel, setMailLabel] = useState('');

  useEffect(() => {
    if (!form) return;
    setName(src?.name || '');
    setUrl(src?.url || '');
    setOrg(src?.org || 'all');
    setFreq(src?.freq || 'daily');
    setSenders((src?.senders || []).join(', '));
    setMailLabel(src?.mailLabel || '');
  }, [form]);

  useEffect(() => {
    if (!form) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [form, onClose]);

  if (!form) return null;
  const close = () => onClose();
  const save = () => {
    if (!name.trim() || !url.trim()) return;
    onSave({
      kind, name: name.trim(), url: url.trim(), org, freq, status: src?.status || 'active',
      ...(isMail
        ? {
            senders: senders.split(',').map((s) => s.trim()).filter((s) => /@/.test(s)),
            mailLabel: mailLabel.trim(),
          }
        : {}),
    });
  };


  const inputStyle = { width: '100%', padding: '11px 13px', border: '1px solid var(--line-2)', background: 'var(--bg-1)', borderRadius: 10, fontSize: 14, color: 'var(--text-1)' };
  const labelStyle = { fontSize: 11.5, color: 'var(--text-3)', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 500 };

  return (
    <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 330, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="card scale-in" onClick={(e) => e.stopPropagation()} style={{ width: 540, maxWidth: '100%', padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--line-1)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="sem sem-violet" style={{ width: 34, height: 34, borderRadius: 9, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {isMail ? <Icon.Mail size={16} /> : <Icon.Globe size={16} />}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {src ? 'Editar' : 'Añadir'} {isMail ? 'buzón de newsletters' : 'fuente web'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {isMail ? 'Lizzi leerá este correo cada mañana.' : 'Lizzi rastreará esta página cada mañana.'}
            </div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={close}><Icon.X size={15} /></button>
        </div>

        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <span style={labelStyle}>Nombre</span>
            <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle}
              placeholder={isMail ? 'Buzón IH Madrid' : 'Fundación X · Convocatorias'} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <span style={labelStyle}>{isMail ? 'Dirección de correo' : 'URL a rastrear'}</span>
            <input value={url} onChange={(e) => setUrl(e.target.value)} style={inputStyle}
              placeholder={isMail ? 'farodeimpacto@gmail.com' : 'https://…'} />
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span style={labelStyle}>Aplica a</span>
              <Select value={org} onChange={setOrg}
                options={[{ value: 'all', label: 'Transversal · todas las organizaciones' },
                  ...orgs.filter((o) => o.id !== 'all').map((o) => ({ value: o.id, label: o.name }))]} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span style={labelStyle}>Frecuencia</span>
              <Select value={freq} onChange={setFreq} options={[
                { value: 'daily', label: 'Diaria · 09:00' },
                { value: 'weekly', label: 'Semanal' },
              ]} />
            </label>
          </div>

          {isMail && (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span style={labelStyle}>Remitentes a vigilar (opcional)</span>
                <input value={senders} onChange={(e) => setSenders(e.target.value)} style={inputStyle}
                  placeholder="butlletins@accio.gencat.cat, info@ejemplo.org" />
                <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>
                  Separados por comas. Si lo dejas vacío, Lizzi lee todos los correos nuevos del buzón.
                </span>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span style={labelStyle}>Etiqueta de Gmail (opcional)</span>
                <input value={mailLabel} onChange={(e) => setMailLabel(e.target.value)} style={inputStyle}
                  placeholder="Newsletters" />
              </label>
            </>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-3)' }}>
            <Icon.Info size={14} /> Solo la organización indicada verá las oportunidades de esta fuente (además de las transversales).
          </div>
        </div>

        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--line-1)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-outline" onClick={close}>Cancelar</button>
          <button className="btn btn-primary" disabled={!name.trim() || !url.trim()} onClick={save}>
            <Icon.Check size={14} /> {src ? 'Guardar cambios' : (isMail ? 'Añadir buzón' : 'Añadir fuente')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------
// Lista de palabras editable (temáticas, formatos de evento,
// ámbitos y exclusiones). Chips con aspa + campo para añadir.
// ---------------------------------------------------------
const KW_TONE = { violet: 'sem sem-violet', lime: 'sem sem-lime', danger: 'sem sem-danger', plain: 'chip' };

function KeywordField({ label, help, values = [], suggestions = [], tone = 'plain', icon: Ico, canEdit = true, onChange }) {
  const [draft, setDraft] = useState('');
  const [openSuggest, setOpenSuggest] = useState(false);
  const cls = KW_TONE[tone] || KW_TONE.plain;
  const add = (raw) => {
    const v = String(raw || '').trim();
    if (!v) return;
    if (!values.some((x) => x.toLowerCase() === v.toLowerCase())) onChange([...values, v]);
    setDraft('');
  };
  const remove = (v) => onChange(values.filter((x) => x !== v));
  const free = suggestions.filter((s) => !values.some((x) => x.toLowerCase() === s.toLowerCase()));

  return (
    <section className="faro-kw">
      <header className="faro-kw-head">
        <span className={`faro-kw-icon tone-${tone}`}>{Ico ? <Ico size={13} /> : <Icon.Tag size={13} />}</span>
        <div style={{ minWidth: 0 }}>
          <div className="faro-kw-label">{label}</div>
          {help && <p className="faro-kw-help">{help}</p>}
        </div>
        <span className="faro-kw-count">{values.length}</span>
      </header>

      <div className="faro-kw-chips">
        {values.map((v) => (
          <span key={v} className={`${cls} faro-kw-chip`}>
            {v}
            {canEdit && (
              <button type="button" onClick={() => remove(v)} aria-label={`Quitar ${v}`} className="faro-kw-x">
                <Icon.X size={10} />
              </button>
            )}
          </span>
        ))}
        {!values.length && <span className="faro-kw-empty">Sin palabras definidas</span>}
      </div>

      {canEdit && (
        <div className="faro-kw-add">
          <div className="faro-kw-field">
            <span className="faro-kw-field-icon"><Icon.Plus size={13} /></span>
            <input
              className="faro-kw-input"
              value={draft}
              placeholder="Añadir palabra y pulsar Enter"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(draft); } }}
            />
            <button type="button" className="faro-kw-submit" onClick={() => add(draft)} disabled={!draft.trim()}>
              Añadir
            </button>
          </div>
          {free.length > 0 && (
            <button
              type="button"
              className={`faro-kw-toggle${openSuggest ? ' is-open' : ''}`}
              onClick={() => setOpenSuggest((v) => !v)}
            >
              Sugerencias
              <span className="faro-kw-toggle-n">{free.length}</span>
              <Icon.ChevronDown size={12} />
            </button>
          )}
        </div>
      )}

      {canEdit && openSuggest && free.length > 0 && (
        <div className="faro-kw-suggest">
          {free.map((s) => (
            <button key={s} type="button" className="faro-kw-sug" onClick={() => add(s)}>
              <Icon.Plus size={10} /> {s}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

// =========================================================
// Pestaña 3 · Criterios
// =========================================================
function CriteriaTab({ criteria, setCriteria, items, orgLabel, canEdit = true, onSave }) {
  const pass = (o) =>
    (criteria.minAmount ? o.amount != null && o.amount >= criteria.minAmount : true) &&
    (daysLeft(o.deadline) ?? -1) >= 0 &&
    o.score >= (criteria.minScore || 0) &&
    ((o.destination === 'evento' && criteria.destinations?.evento) || (o.destination === 'proyecto' && criteria.destinations?.proyecto));
  const passing = items.filter(pass).length;
  const pct = items.length ? Math.round((passing / items.length) * 100) : 0;

  const baseline = useRef(null);
  useEffect(() => { baseline.current = JSON.stringify(criteria); }, [criteria.id]);
  const dirty = baseline.current != null && baseline.current !== JSON.stringify(criteria);

  const setList = (key, next) => setCriteria((c) => ({ ...c, [key]: next }));

  const SUGGEST_THEMES = ['Emprendimiento', 'Innovación', 'Economía social', 'Sostenibilidad', 'Formación', 'Empleo e inclusión', 'Cultura', 'Culturales y Creativas', 'Comunicación', 'Regenerativo', 'Mediación Artística', 'Mediación Cultural', 'Digitalización', 'Igualdad'];
  const SUGGEST_EVENTS = ['Jornada', 'Encuentro', 'Feria', 'Evento', 'Congreso', 'Exposiciones', 'Visita', 'Tour', 'Foros', 'Premio', 'Hackathons', 'Celebraciones', 'Networking'];

  return (
    <div className="faro-crit">
      <div className="faro-crit-main">
        {/* 1 · Qué buscamos */}
        <div className="card faro-crit-card">
          <div className="faro-crit-card-head">
            <span className="faro-crit-step">1</span>
            <div style={{ minWidth: 0 }}>
              <div className="faro-crit-title">Qué buscamos</div>
              <p className="faro-crit-sub">Palabras que suben el encaje y deciden si es un evento. Se aplican a <strong>{orgLabel}</strong>.</p>
            </div>
          </div>
          <div className="faro-crit-body">
            <KeywordField
              label="Temáticas de interés"
              help="Suben el nivel de encaje cuando aparecen en la convocatoria."
              tone="violet" icon={Icon.Tag} canEdit={canEdit}
              values={criteria.themes || []} suggestions={SUGGEST_THEMES}
              onChange={(next) => setList('themes', next)} />
            <KeywordField
              label="Formatos de evento"
              help="Si la convocatoria menciona alguna, se clasifica como Evento."
              tone="lime" icon={Icon.Calendar} canEdit={canEdit}
              values={criteria.eventKeywords || []} suggestions={SUGGEST_EVENTS}
              onChange={(next) => setList('eventKeywords', next)} />
          </div>
        </div>

        {/* 2 · Umbrales */}
        <div className="card faro-crit-card">
          <div className="faro-crit-card-head">
            <span className="faro-crit-step">2</span>
            <div style={{ minWidth: 0 }}>
              <div className="faro-crit-title">Umbrales</div>
              <p className="faro-crit-sub">Mínimos que una convocatoria debe cumplir para llegar a la bandeja.</p>
            </div>
          </div>
          <div className="faro-crit-body">
            <div className="faro-crit-grid">
              <RangeField label="Importe mínimo" value={criteria.minAmount} min={0} max={200000} step={5000}
                minLabel="Sin mínimo" maxLabel="200.000 €" disabled={!canEdit}
                display={criteria.minAmount ? fmtMoney(criteria.minAmount) : 'Sin mínimo'}
                onChange={(v) => setCriteria((c) => ({ ...c, minAmount: v }))} />
              <RangeField label="Plazo mínimo hasta el cierre" value={criteria.minDays} min={0} max={90} step={5}
                minLabel="0 días" maxLabel="90 días" disabled={!canEdit}
                display={`${criteria.minDays} días`} onChange={(v) => setCriteria((c) => ({ ...c, minDays: v }))} />
              <RangeField label="Encaje mínimo para alertar" value={criteria.minScore} min={0} max={100} step={5}
                minLabel="0" maxLabel="100" disabled={!canEdit}
                display={`${criteria.minScore}/100`} onChange={(v) => setCriteria((c) => ({ ...c, minScore: v }))} />
              <div className="faro-crit-field">
                <div className="faro-crit-field-head"><span className="faro-crit-field-label">Destino</span></div>
                <div className="faro-crit-dest">
                  {['evento', 'proyecto'].map((d) => {
                    const on = !!criteria.destinations?.[d];
                    return (
                      <button key={d} type="button" className={`faro-crit-toggle${on ? ' is-on' : ''}`} disabled={!canEdit}
                        onClick={() => setCriteria((c) => ({ ...c, destinations: { ...c.destinations, [d]: !on } }))}>
                        {on ? <Icon.Check size={12} /> : <Icon.Plus size={12} />} {DEST_META[d].label}
                      </button>
                    );
                  })}
                </div>
                <p className="faro-crit-field-help">Al menos uno debe estar activo.</p>
              </div>
            </div>
          </div>
        </div>

        {/* 3 · Límites */}
        <div className="card faro-crit-card">
          <div className="faro-crit-card-head">
            <span className="faro-crit-step">3</span>
            <div style={{ minWidth: 0 }}>
              <div className="faro-crit-title">Ámbito y exclusiones</div>
              <p className="faro-crit-sub">Dónde miramos y qué descartamos sin revisar.</p>
            </div>
          </div>
          <div className="faro-crit-body">
            <KeywordField
              label="Ámbito geográfico"
              help="Territorios cuyas convocatorias nos interesan."
              tone="plain" icon={Icon.Globe} canEdit={canEdit}
              values={criteria.scopes || []}
              onChange={(next) => setList('scopes', next)} />
            <KeywordField
              label="Palabras de exclusión"
              help="Descartan la convocatoria cuando aparecen."
              tone="danger" icon={Icon.X} canEdit={canEdit}
              values={criteria.exclude || []}
              onChange={(next) => setList('exclude', next)} />
          </div>
        </div>

        <div className="card card-pad faro-crit-soon">
          <Icon.Layers size={15} style={{ color: 'var(--text-3)' }} />
          <div>
            <div className="faro-crit-title" style={{ fontSize: 13 }}>Criterios avanzados</div>
            <p className="faro-crit-sub">Scoring ponderado, exclusiones por fundación y reglas por sociedad.</p>
          </div>
          <span className="chip faro-crit-soon-chip">Próximamente</span>
        </div>
      </div>

      <aside className="faro-crit-side">
        <div className="card faro-crit-card">
          <div className="faro-crit-card-head">
            <div style={{ minWidth: 0 }}>
              <div className="faro-crit-title">Vista previa del filtro</div>
              <p className="faro-crit-sub">Sobre el histórico actual.</p>
            </div>
          </div>
          <div className="faro-crit-body">
            <div className="faro-crit-big">
              <span className="faro-crit-num">{passing}</span>
              <span className="faro-crit-of">de {items.length}</span>
            </div>
            <div className="faro-crit-bar"><div style={{ width: `${pct}%` }} /></div>
            <div className="faro-crit-pct">{pct}% de las convocatorias pasarían estos criterios.</div>

            <ul className="faro-crit-recap">
              <li><span>Temáticas</span><b>{(criteria.themes || []).length}</b></li>
              <li><span>Formatos de evento</span><b>{(criteria.eventKeywords || []).length}</b></li>
              <li><span>Importe mínimo</span><b>{criteria.minAmount ? fmtMoney(criteria.minAmount) : 'Sin mínimo'}</b></li>
              <li><span>Plazo mínimo</span><b>{criteria.minDays} días</b></li>
              <li><span>Encaje mínimo</span><b>{criteria.minScore}/100</b></li>
              <li><span>Exclusiones</span><b>{(criteria.exclude || []).length}</b></li>
            </ul>

            <p className="faro-crit-note">Afectan a la bandeja y al resumen diario por correo.</p>
          </div>

          {canEdit && (
            <div className="faro-crit-save">
              <span className={`faro-crit-dirty${dirty ? ' is-on' : ''}`}>{dirty ? 'Cambios sin guardar' : 'Todo guardado'}</span>
              <button className="btn btn-primary" onClick={onSave} disabled={!dirty}>
                <Icon.Save size={13} /> Guardar
              </button>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function RangeField({ label, value, min, max, step, display, minLabel, maxLabel, disabled, onChange }) {
  return (
    <div className="faro-crit-field">
      <div className="faro-crit-field-head">
        <span className="faro-crit-field-label">{label}</span>
        <span className="faro-crit-value font-mono">{display}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value || 0} disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))} className="faro-crit-range" />
      <div className="faro-crit-scale"><span>{minLabel ?? min}</span><span>{maxLabel ?? max}</span></div>
    </div>
  );
}

// =========================================================
// Pestaña 4 · Alertas
// =========================================================
function AlertsTab({ alerts, setAlerts, orgs, items, schedule, canEdit = true, onSaveAlerts, onSaveSchedule }) {
  const preview = items
    .filter((o) => o.status !== 'discarded' && (daysLeft(o.deadline) ?? -1) >= 0)
    .slice(0, 4);

  const toggleInclude = (k) => setAlerts((a) => ({ ...a, include: { ...a.include, [k]: !a.include[k] } }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
    <AutomationCard schedule={schedule} canEdit={canEdit} onSave={onSaveSchedule} />
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 20, alignItems: 'start' }}>

      <div className="card card-pad">
        <div className="section-title" style={{ marginBottom: 4 }}>Resumen diario por correo</div>
        <p className="muted" style={{ fontSize: 12.5, marginBottom: 16 }}>Un único correo cada mañana con las convocatorias abiertas que encajan.</p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 10, background: 'var(--bg-2)', border: '1px solid var(--line-1)', marginBottom: 16 }}>
          <Icon.Bell size={15} style={{ color: alerts.enabled ? 'var(--primary)' : 'var(--text-4)' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Alertas activas</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{alerts.enabled ? 'Se envía cada día laborable' : 'Envío pausado'}</div>
          </div>
          <button className={alerts.enabled ? 'btn btn-primary' : 'btn btn-outline'} style={{ fontSize: 12 }}
            onClick={() => setAlerts((a) => ({ ...a, enabled: !a.enabled }))}>
            {alerts.enabled ? 'Activadas' : 'Desactivadas'}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <span style={{ fontSize: 12.5, fontWeight: 600 }}>Hora de envío</span>
            <Select value={alerts.hour} onChange={(v) => setAlerts((a) => ({ ...a, hour: v }))}
              options={['08:00', '09:00', '10:00', '13:00']} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <span style={{ fontSize: 12.5, fontWeight: 600 }}>Días</span>
            <Select value={alerts.weekdaysOnly ? 'wd' : 'all'} onChange={(v) => setAlerts((a) => ({ ...a, weekdaysOnly: v === 'wd' }))}
              options={[{ value: 'wd', label: 'Solo días laborables' }, { value: 'all', label: 'Todos los días' }]} />
          </label>
        </div>

        <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 8 }}>Qué incluir</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 18 }}>
          {[['new', 'Nuevas detectadas'], ['closingSoon', 'Cierran pronto'], ['interesting', 'Marcadas interesantes'], ['discarded', 'Descartadas']].map(([k, l]) => {
            const on = !!alerts.include?.[k];
            return (
              <button key={k} onClick={() => toggleInclude(k)} className={on ? 'sem sem-violet' : 'chip'}
                style={{ fontSize: 11.5, cursor: 'pointer', border: on ? undefined : '1px solid var(--line-2)' }}>
                {on && <Icon.Check size={11} />} {l}
              </button>
            );
          })}
        </div>

        <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 8 }}>Destinatarios</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {(alerts.recipients || []).map((r, i) => {
            const org = orgs.find((o) => o.id === r.org) || {};
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 12px', borderRadius: 9, border: '1px solid var(--line-1)', background: 'var(--bg-2)' }}>
                <Icon.Mail size={13} style={{ color: 'var(--text-4)' }} />
                <span style={{ fontSize: 12.5 }}>{r.email}</span>
                <span className="chip" style={{ marginLeft: 'auto', fontSize: 10 }}>{r.org === 'all' ? 'Transversal' : (org.short || org.name)}</span>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18, gap: 8 }}>
          {canEdit && onSaveAlerts && (
            <button className="btn btn-primary" onClick={onSaveAlerts}><Icon.Save size={13} /> Guardar alertas</button>
          )}
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line-1)', display: 'flex', alignItems: 'center', gap: 9 }}>
          <Icon.Eye size={14} style={{ color: 'var(--text-3)' }} />
          <div style={{ fontSize: 13.5, fontWeight: 600 }}>Vista previa del correo</div>
          <span className="chip" style={{ marginLeft: 'auto', fontSize: 10 }}>{alerts.hour} · diario</span>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ fontSize: 12, color: 'var(--text-4)', marginBottom: 4 }}>Asunto</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>El Faro · {preview.length} oportunidades abiertas para hoy</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {preview.map((o) => {
              const de = DEST_META[o.destination] || DEST_META.proyecto;
              const dl = daysLeft(o.deadline);
              return (
                <div key={o.id} style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid var(--line-1)', background: 'var(--bg-2)' }}>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span className="chip" style={{ fontSize: 10 }}>{(KIND_META[o.kind] || {}).label}</span>
                    <span className={`sem ${de.sem}`} style={{ fontSize: 10 }}>{de.label}</span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35 }}>{o.title}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 4 }}>
                    {o.funder} · {o.amount == null ? 'dotación no publicada' : fmtMoney(o.amount)} · cierra {fmtDateFull(o.deadline)}{dl != null && dl >= 0 ? ` (${dl} días)` : ''}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-4)', marginTop: 4 }}>{(o.requirements || [])[0]}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--primary)', marginTop: 6 }}>Ver convocatoria →</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}

// ---------- Panel de automatización (solo Admin) ----------
const WEEKDAY_LABELS = [['1', 'L'], ['2', 'M'], ['3', 'X'], ['4', 'J'], ['5', 'V'], ['6', 'S'], ['7', 'D']];

function AutomationCard({ schedule, canEdit, onSave }) {
  const s = schedule || {};
  const weekdays = s.weekdays || [1, 2, 3, 4, 5];
  const patch = (p) => onSave && onSave(p);

  return (
    <div className="card card-pad">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <div className="section-title" style={{ margin: 0 }}>Rastreo automático</div>
        {!canEdit && <span className="chip" style={{ fontSize: 10.5, marginLeft: 'auto' }}><Icon.Eye size={10} /> Solo Admin</span>}
      </div>
      <p className="muted" style={{ fontSize: 12.5, marginBottom: 18, maxWidth: 640 }}>
        Si lo desactivas, el rastreo solo se ejecuta al pulsar «Rastrear ahora».
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 15px', borderRadius: 10, background: 'var(--bg-2)', border: '1px solid var(--line-1)', marginBottom: 18 }}>
        <Icon.Refresh size={15} style={{ color: s.enabled ? 'var(--primary)' : 'var(--text-4)' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{s.enabled ? 'Automatización activada' : 'Automatización desactivada'}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
            {s.enabled
              ? `Se ejecuta a las ${nextRunLabel(s)} (${s.timezone || 'Europe/Madrid'})`
              : 'Ningún rastreo programado'}
          </div>
        </div>
        <button className={s.enabled ? 'btn btn-primary' : 'btn btn-outline'} disabled={!canEdit}
          style={{ fontSize: 12 }} onClick={() => patch({ enabled: !s.enabled })}>
          {s.enabled ? 'Activada' : 'Desactivada'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 18 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600 }}>Hora</span>
          <Select value={s.run_hour ?? 9} disabled={!canEdit} onChange={(v) => patch({ run_hour: Number(v) })}
            options={Array.from({ length: 24 }, (_, h) => ({ value: h, label: `${String(h).padStart(2, '0')}:00` }))} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600 }}>Minuto</span>
          <Select value={s.run_minute ?? 0} disabled={!canEdit} onChange={(v) => patch({ run_minute: Number(v) })}
            options={[0, 10, 15, 20, 30, 40, 45, 50].map((m) => ({ value: m, label: String(m).padStart(2, '0') }))} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600 }}>Zona horaria</span>
          <Select value={s.timezone || 'Europe/Madrid'} disabled={!canEdit} onChange={(v) => patch({ timezone: v })}
            options={['Europe/Madrid', 'Europe/Lisbon', 'UTC']} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600 }}>Máx. páginas por rastreo</span>
          <Select value={s.max_pages_per_run ?? 40} disabled={!canEdit} onChange={(v) => patch({ max_pages_per_run: Number(v) })}
            options={[10, 20, 40, 60, 100].map((n) => ({ value: n, label: `${n} páginas` }))} />
        </label>
      </div>

      <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 8 }}>Días de ejecución</div>
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
        {WEEKDAY_LABELS.map(([v, l]) => {
          const n = Number(v);
          const on = weekdays.includes(n);
          return (
            <button key={v} disabled={!canEdit} className={on ? 'sem sem-violet' : 'chip'}
              style={{ width: 38, justifyContent: 'center', fontSize: 12, cursor: canEdit ? 'pointer' : 'default', border: on ? undefined : '1px solid var(--line-2)' }}
              onClick={() => patch({ weekdays: on ? weekdays.filter((x) => x !== n) : [...weekdays, n].sort() })}>
              {l}
            </button>
          );
        })}
      </div>

      {s.paused_reason && (
        <div className="sem sem-danger" style={{ fontSize: 12, marginTop: 16, padding: '10px 12px', borderRadius: 9, whiteSpace: 'normal' }}>
          <Icon.AlertTriangle size={13} /> En pausa automática: {s.paused_reason}
          {canEdit && (
            <button className="btn btn-ghost" style={{ marginLeft: 'auto', fontSize: 11.5 }}
              onClick={() => patch({ paused_reason: null, paused_at: null })}>Reanudar</button>
          )}
        </div>
      )}

      {s.last_run_at && (
        <div style={{ fontSize: 11.5, color: 'var(--text-4)', marginTop: 16 }}>
          Última ejecución {relDay(s.last_run_at)} · estado {s.last_run_status || '—'}
        </div>
      )}
    </div>
  );
}

// =========================================================
// Overlay de rastreo simulado
// =========================================================
function ScanOverlay({ open, steps, onDone }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!open) { setIdx(0); return; }
    if (!steps.length) { onDone(); return; }
    const t = setTimeout(() => {
      if (idx >= steps.length - 1) onDone();
      else setIdx((i) => i + 1);
    }, 900);
    return () => clearTimeout(t);
  }, [open, idx, steps.length, onDone]);

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(5px)', zIndex: 340, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="card scale-in" style={{ width: 420, maxWidth: '100%', padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <span className="sem sem-lime" style={{ width: 34, height: 34, borderRadius: 10, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icon.Lighthouse size={17} /></span>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Rastreando fuentes</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>El Faro está revisando webs y buzones.</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {steps.map((s, i) => {
            const done = i < idx;
            const active = i === idx;
            return (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: done || active ? 1 : 0.45 }}>
                <span style={{
                  width: 22, height: 22, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: done ? 'var(--sem-success-bg)' : active ? 'var(--primary-soft)' : 'var(--bg-2)',
                  color: done ? 'var(--sem-success)' : active ? 'var(--primary)' : 'var(--text-4)',
                  border: '1px solid var(--line-1)', flexShrink: 0,
                }}>
                  {done ? <Icon.Check size={12} /> : active ? <Icon.Refresh size={12} /> : <Icon.Dot size={12} />}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: 'var(--text-1)' }}>{s.label}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{s.detail}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ height: 5, borderRadius: 99, background: 'var(--line-1)', marginTop: 20, overflow: 'hidden' }}>
          <div style={{ width: `${((idx + 1) / Math.max(steps.length, 1)) * 100}%`, height: '100%', background: 'var(--primary)', transition: 'width 300ms ease' }} />
        </div>
      </div>
    </div>
  );
}

// =========================================================
// Enviar a análisis · busca las bases en la página oficial
// =========================================================
function SendToBidModal({ opp, demoMode, onClose, onConfirm }) {
  const [lot, setLot] = useState('');
  const [docs, setDocs] = useState([]);
  const [picked, setPicked] = useState(() => new Set());
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!opp) return;
    setLot(''); setDocs([]); setPicked(new Set()); setFiles([]); setNote(''); setSending(false);
    if (demoMode || !opp.url) { setNote(opp.url ? '' : 'Esta convocatoria no tiene enlace: sube los documentos a mano.'); return; }
    const cached = (opp.documents || []).filter((d) => d && d.url);
    if (opp.documentsFetchedAt && cached.length) {
      setDocs(cached);
      setPicked(new Set(cached.filter((d) => d.kind !== 'page').slice(0, 6).map((d) => d.url)));
      return;
    }
    let alive = true;
    setBusy(true);
    fetchOppDocs(opp.url, opp.id)
      .then((res) => {
        if (!alive) return;
        const found = (res?.documents || res?.docs || []).filter((d) => d.url);
        setDocs(found);
        setPicked(new Set(found.filter((d) => d.kind !== 'page').slice(0, 6).map((d) => d.url)));
        setNote(res?.note || (found.length ? '' : 'No se encontraron documentos descargables en la página.'));
      })
      .catch((e) => { if (alive) setNote(`No se pudo leer la página: ${e.message || e}`); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [opp, demoMode]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!opp) return null;

  const toggle = (url) => setPicked((prev) => { const n = new Set(prev); n.has(url) ? n.delete(url) : n.add(url); return n; });
  const total = picked.size + files.length;

  const submit = async () => {
    if (sending) return;
    setSending(true);
    await onConfirm({ lot: lot || null, docUrls: [...picked], files });
    setSending(false);
  };

  const label = (t) => (
    <div style={{ fontSize: 11, color: 'var(--text-3)', letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 600, marginBottom: 10 }}>{t}</div>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)', zIndex: 335, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div className="card scale-in" onClick={(e) => e.stopPropagation()} style={{ width: 640, maxWidth: '100%', padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '84vh' }}>

        {/* Cabecera */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--line-1)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <span className="sem sem-violet" style={{ width: 36, height: 36, borderRadius: 10, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon.Sparkles size={17} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 650, lineHeight: 1.3 }}>Enviar a análisis</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 3, lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{opp.title}</div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Cerrar" style={{ flexShrink: 0 }}><Icon.X size={15} /></button>
        </div>

        {/* Cuerpo */}
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 22, overflow: 'auto' }}>

          {/* Resumen de la convocatoria */}
          <div style={{ border: '1px solid var(--line-2)', borderRadius: 12, background: 'var(--bg-1)', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <Icon.Building size={13} style={{ color: 'var(--text-4)', flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opp.funder || '—'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <Icon.Calendar size={13} style={{ color: 'var(--text-4)', flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{opp.deadline ? `Cierra ${opp.deadline}` : 'Sin fecha de cierre'}</span>
              </div>
            </div>
            {opp.url && (
              <a className="btn btn-outline" href={opp.url} target="_blank" rel="noopener noreferrer nofollow" style={{ alignSelf: 'flex-start', padding: '6px 11px', fontSize: 12 }}>
                <Icon.ArrowUpRight size={13} /> Ver convocatoria oficial
              </a>
            )}
          </div>

          {/* Lote */}
          <div>
            {label('Lote (opcional)')}
            <Select value={lot} onChange={setLot} icon="Layers"
              options={[{ v: '', l: 'Sin lotes' }, ...LOT_OPTIONS.map((l) => ({ v: l, l }))]} />
          </div>

          {/* Documentos */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 11, color: 'var(--text-3)', letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 600 }}>Documentos de la convocatoria</span>
              {busy
                ? <span className="spin" style={{ display: 'inline-flex', color: 'var(--text-4)' }}><Icon.Refresh size={13} /></span>
                : docs.length > 0 && <span className="font-mono" style={{ fontSize: 11, color: 'var(--text-4)' }}>{picked.size}/{docs.length}</span>}
            </div>

            {busy && docs.length === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[0, 1, 2].map((i) => (
                  <div key={i} className="skeleton" style={{ height: 42, borderRadius: 10, background: 'var(--bg-2)', opacity: 0.6 - i * 0.15 }} />
                ))}
              </div>
            )}

            {docs.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {docs.map((d) => {
                  const on = picked.has(d.url);
                  return (
                    <label key={d.url} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', border: `1px solid ${on ? 'var(--primary)' : 'var(--line-2)'}`, borderRadius: 10, background: on ? 'var(--primary-soft)' : 'var(--bg-1)', cursor: 'pointer', transition: 'background .15s ease, border-color .15s ease' }}>
                      <span style={{ width: 17, height: 17, borderRadius: 5, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: `1.5px solid ${on ? 'var(--primary)' : 'var(--line-3)'}`, background: on ? 'var(--primary)' : 'transparent', color: 'var(--primary-fg)' }}>
                        {on && <Icon.Check size={11} />}
                      </span>
                      <input type="checkbox" checked={on} onChange={() => toggle(d.url)} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                      <Icon.FileText size={14} style={{ color: 'var(--text-4)', flexShrink: 0 }} />
                      <span style={{ fontSize: 13, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{d.name}</span>
                      {d.size ? <span className="font-mono" style={{ fontSize: 11, color: 'var(--text-4)', flexShrink: 0 }}>{Math.round(d.size / 1024)} KB</span> : null}
                    </label>
                  );
                })}
              </div>
            )}

            {!busy && note && (
              <div className="sem sem-warning" style={{ fontSize: 12.5, padding: '11px 13px', borderRadius: 10, marginTop: docs.length ? 10 : 0, display: 'flex', alignItems: 'flex-start', gap: 8, lineHeight: 1.45 }}>
                <Icon.Info size={13} style={{ flexShrink: 0, marginTop: 2 }} /> <span>{note}</span>
              </div>
            )}
          </div>

          {/* Subida manual */}
          <div>
            {label('Añadir documentos a mano')}
            <label className="btn btn-outline" style={{ cursor: 'pointer', width: '100%', justifyContent: 'center', padding: '11px 13px', borderStyle: 'dashed' }}>
              <Icon.Upload size={14} /> Seleccionar archivos del ordenador
              <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.odt"
                onChange={(e) => setFiles([...(e.target.files || [])])}
                style={{ display: 'none' }} />
            </label>
            {files.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                {files.map((f, i) => (
                  <div key={`${f.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 13px', border: '1px solid var(--line-2)', borderRadius: 10, background: 'var(--bg-1)' }}>
                    <Icon.Paperclip size={13} style={{ color: 'var(--text-4)', flexShrink: 0 }} />
                    <span style={{ fontSize: 12.5, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{f.name}</span>
                    <span className="font-mono" style={{ fontSize: 11, color: 'var(--text-4)', flexShrink: 0 }}>{Math.round(f.size / 1024)} KB</span>
                    <button className="btn btn-ghost btn-icon" aria-label="Quitar" style={{ flexShrink: 0 }}
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}><Icon.X size={13} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pie */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--line-1)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text-4)' }}>{total} documento{total === 1 ? '' : 's'} para Lizzi</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={submit} disabled={sending || busy}>
              {sending ? <span className="spin" style={{ display: 'inline-flex' }}><Icon.Refresh size={13} /></span> : <Icon.Sparkles size={13} />}
              {sending ? 'Creando…' : 'Crear y analizar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FaroScreen;

